/**
 * A ÁGUA TRADUZIDA EM DECISÃO.
 *
 * Duas perguntas, e elas são diferentes — foi a correção mais importante que o
 * gestor fez no desenho:
 *
 *   O OXIGÊNIO responde QUANTO se oferece. Corta quantidade, e o corte vale
 *   para o dia. A água é um estado que se arrasta, e a ração de hoje é uma das
 *   causas do oxigênio de amanhã: liberar tudo ao meio-dia porque o número
 *   subiu é gastar amanhã para comer hoje.
 *
 *   A TEMPERATURA responde QUANDO. Água fria de manhã não é motivo para
 *   oferecer menos — é motivo para oferecer mais tarde, quando o sol
 *   esquentar. Só corta nos extremos.
 *
 * Nenhuma faixa está escrita aqui. Todas vêm da tabela `coeficiente_ambiental`,
 * pelo servidor, porque outra piscicultura tem outra água.
 */

import type { FaixaDeCoeficiente } from '../acesso/api'

export type QuemLimitou = 'oxigenio' | 'temperatura' | 'sem_leitura'
export type Tendencia = 'subindo' | 'estavel' | 'caindo' | 'sem_serie'

export interface Leitura {
  data: string
  hora: string | null
  temperatura_c: number | null
  oxigenio_mgl: number | null
  ceu: string | null
}

export interface Veredito {
  coeficiente: number
  /** Quem puxou o número para baixo. `null` quando nada limitou. */
  limitou: QuemLimitou | null
  /** `adiar` quer dizer «espere e releia», não «ofereça menos». */
  acao: 'reduzir' | 'adiar' | null
  /** O valor que mandou, para a tela poder dizer o porquê. */
  valor: number | null
}

/** Acha a faixa que contém o valor. Faixa é [de, ate); `ate` vazio é sem teto. */
function faixaDe(
  valor: number,
  fator: FaixaDeCoeficiente['fator'],
  faixas: FaixaDeCoeficiente[],
): FaixaDeCoeficiente | null {
  return (
    faixas.find(
      (f) => f.fator === fator && valor >= f.de && (f.ate === null || valor < f.ate),
    ) ?? null
  )
}

/**
 * O veredito do dia para um tanque.
 *
 * MANDA O MENOR dos dois, e não o produto. Multiplicar 50% por 50% daria 25% —
 * punição dupla pelo mesmo dia ruim, sem que nada na água justifique a segunda.
 * E o menor tem uma vantagem que a conta não tem: a tela consegue dizer QUEM
 * mandou. «50%, quem limitou foi o oxigênio, 2,4 mg/L» é frase que o tratador
 * entende e o gestor confere. «25%» não é.
 */
export function vereditoDaAgua(
  oxigenioBruto: number | null,
  temperaturaBruta: number | null,
  faixas: FaixaDeCoeficiente[],
): Veredito {
  // NÚMERO QUE NÃO É NÚMERO CONTA COMO AUSENTE.
  //
  // O gestor digitou «2.9\» no campo do oxigênio e o app respondeu «água boa
  // — trato cheio». A explicação é curta e o estrago não seria: `Number()`
  // devolve NaN, NaN não é menor nem maior que coisa alguma, então ele não cai
  // em faixa nenhuma — e a ausência de faixa que penalize era lida como
  // ausência de problema.
  //
  // Um valor impossível não pode nunca virar permissão. Aqui ele vira
  // ausência, que é o que de fato é: ninguém mediu nada.
  const oxigenio = Number.isFinite(oxigenioBruto) ? oxigenioBruto : null
  const temperatura = Number.isFinite(temperaturaBruta) ? temperaturaBruta : null

  if (oxigenio === null && temperatura === null) {
    return { coeficiente: 1, limitou: 'sem_leitura', acao: null, valor: null }
  }

  const candidatos: Veredito[] = []

  if (oxigenio !== null) {
    const f = faixaDe(oxigenio, 'oxigenio_mgl', faixas)
    if (f) {
      candidatos.push({
        coeficiente: f.coeficiente,
        limitou: 'oxigenio',
        acao: f.acao,
        valor: oxigenio,
      })
    }
  }

  if (temperatura !== null) {
    const f = faixaDe(temperatura, 'temperatura_c', faixas)
    if (f) {
      candidatos.push({
        coeficiente: f.coeficiente,
        limitou: 'temperatura',
        acao: f.acao,
        valor: temperatura,
      })
    }
  }

  if (candidatos.length === 0) {
    return { coeficiente: 1, limitou: null, acao: null, valor: null }
  }

  const pior = candidatos.reduce((a, b) => (b.coeficiente < a.coeficiente ? b : a))
  if (pior.coeficiente >= 1) return { ...pior, limitou: null, acao: null }
  return pior
}

/**
 * Para onde o oxigênio deste tanque está indo.
 *
 * Compara a média das `n` leituras mais recentes com a das `n` anteriores. Foi
 * escolhido em vez de uma reta ajustada por um motivo prático: «a média destes
 * dias contra a dos anteriores» é frase que o tratador e o gestor conferem de
 * cabeça. Inclinação de reta, não.
 *
 * Sem série suficiente, devolve `sem_serie` — que a tela trata como estável, o
 * neutro. Chutar tendência com três pontos seria inventar direção.
 */
export function tendenciaDoOxigenio(
  leituras: Leitura[],
  n = 3,
  toleranciaPct = 10,
): Tendencia {
  const valores = leituras
    .filter((l) => l.oxigenio_mgl !== null)
    .map((l) => l.oxigenio_mgl as number)

  if (valores.length < n * 2) return 'sem_serie'

  const media = (v: number[]) => v.reduce((s, x) => s + x, 0) / v.length
  const recentes = media(valores.slice(0, n))
  const anteriores = media(valores.slice(n, n * 2))
  if (anteriores === 0) return 'sem_serie'

  const variacao = ((recentes - anteriores) / anteriores) * 100
  if (variacao > toleranciaPct) return 'subindo'
  if (variacao < -toleranciaPct) return 'caindo'
  return 'estavel'
}

/**
 * Qual percentual do peso vivo oferecer, dentro da faixa da tabela.
 *
 * Regra do gestor: a tendência da água escolhe. Oxigênio subindo, o peixe pode
 * comer o máximo; caindo, o mínimo; estável, a média. É o jeito de deixar a
 * água decidir dentro do que o peixe aceita, em vez de escolher uma coluna fixa
 * para sempre.
 */
export function pctDoPesoVivo(
  faixa: { pct_pv_min: number; pct_pv_max: number },
  tendencia: Tendencia,
): number {
  if (tendencia === 'subindo') return faixa.pct_pv_max
  if (tendencia === 'caindo') return faixa.pct_pv_min
  return (faixa.pct_pv_min + faixa.pct_pv_max) / 2
}

/** Texto curto para a tela, em português de gente. */
export function tendenciaEmPalavras(t: Tendencia): string {
  if (t === 'subindo') return 'oxigênio subindo'
  if (t === 'caindo') return 'oxigênio caindo'
  if (t === 'estavel') return 'oxigênio estável'
  return 'série curta demais para dizer'
}

/**
 * O teto daquele tanque naquele mês, em kg/ha/dia.
 *
 * `tanque_id` vazio vale para todos; a linha com tanque manda. E a faixa de
 * meses VIRA O ANO: águas é de novembro a abril, então `mes_inicio` 11 e
 * `mes_fim` 4 cobrem 11, 12, 1, 2, 3 e 4. Tratar isso como intervalo simples
 * deixaria dezembro, janeiro e fevereiro sem teto nenhum — justamente os três
 * meses de chuvarada.
 */
export function tetoDoTanque(
  tetos: { tanque_id: string | null; mes_inicio: number; mes_fim: number; kg_ha_dia: number }[],
  tanqueId: string,
  mes: number,
): number | null {
  const cobre = (de: number, ate: number) =>
    de <= ate ? mes >= de && mes <= ate : mes >= de || mes <= ate

  const doTanque = tetos.find((t) => t.tanque_id === tanqueId && cobre(t.mes_inicio, t.mes_fim))
  if (doTanque) return doTanque.kg_ha_dia

  const geral = tetos.find((t) => t.tanque_id === null && cobre(t.mes_inicio, t.mes_fim))
  return geral ? geral.kg_ha_dia : null
}

// ---------------------------------------------------------------------------
// A ÁGUA QUE ENTRA NA CONTA — uma medida de cada vez
// ---------------------------------------------------------------------------

/** Uma medida usada na conta, e de quando ela é. */
export interface MedidaUsada {
  valor: number
  data: string
  hora: string | null
  /** Medida no próprio dia; falso quando é o eco da última conhecida. */
  doDia: boolean
}

export interface AguaDaConta {
  oxigenio: MedidaUsada | null
  temperatura: MedidaUsada | null
}

/**
 * «SEM LEITURA NO DIA, VALE A ÚLTIMA CONHECIDA» — e o eco é do OXIGÊNIO.
 *
 * A regra é do gestor e continua a mesma. O que mudou em 04/10/2026: antes
 * valia a última LINHA, e uma linha do dia com só temperatura (a Perdas grava
 * assim, e a Ronda aceita) virava «a leitura do dia» sem oxigênio — o 2,4
 * mg/L de ontem sumia da conta e o tanque levava ração cheia. Achado na
 * revisão do dia em que pH e alcalinidade viraram análise avulsa, porque a
 * pergunta do gestor era exatamente essa: o que, além do oxigênio, pode
 * mexer no cálculo do trato?
 *
 * DENTRO DO DIA, CADA MEDIDA VALE PELA SUA LEITURA MAIS RECENTE — e uma
 * leitura nova não apaga a outra medida. Regra do gestor, 04/10/2026, do caso
 * real: grava-se o oxigênio, nota-se que faltou a temperatura, e grava-se só
 * ela. A segunda leitura não pode esconder o oxigênio da primeira; as duas
 * medidas valem juntas.
 *
 * ENTRE DIAS, SÓ O OXIGÊNIO TEM ECO — também do gestor, no mesmo dia: «o
 * oxigênio deixa eco, a temperatura não». Sem oxigênio hoje, vale o último
 * conhecido; sem temperatura hoje, não há temperatura. Ela responde QUANDO
 * tratar, não quanto — a faixa fria manda adiar e reler —, e com eco a
 * friagem de uma manhã de julho mandaria «não tratar» por semanas.
 *
 * O preço, dito para não se perder: no mesmo dia, a temperatura fria das seis
 * continua valendo às três se ninguém reler a TEMPERATURA. Por isso as telas
 * dizem de quando é o número que limitou — a Ronda na resposta enquanto se
 * digita («25,5 °C das 06:00»), o Trato na linha da água —, e quem lê sabe o
 * que reler. O oxímetro mede as duas juntas.
 *
 * Recebe as leituras da mais recente para a mais antiga — a ordem em que o
 * servidor as manda. Leitura posterior ao dia escolhido não entra (o futuro
 * não decide o passado).
 */
export function aguaDaConta(
  leituras: {
    data: string
    hora: string | null
    oxigenio_mgl: number | null
    temperatura_c: number | null
  }[],
  dia: string,
): AguaDaConta {
  const ate = leituras.filter((l) => l.data.slice(0, 10) <= dia)
  const medida = (l: (typeof ate)[number], v: number): MedidaUsada => {
    const data = l.data.slice(0, 10)
    return { valor: v, data, hora: l.hora, doDia: data === dia }
  }

  let oxigenio: MedidaUsada | null = null
  for (const l of ate) {
    if (l.oxigenio_mgl !== null && Number.isFinite(l.oxigenio_mgl)) {
      oxigenio = medida(l, l.oxigenio_mgl)
      break
    }
  }
  let temperatura: MedidaUsada | null = null
  for (const l of ate) {
    if (l.data.slice(0, 10) !== dia) break
    if (l.temperatura_c !== null && Number.isFinite(l.temperatura_c)) {
      temperatura = medida(l, l.temperatura_c)
      break
    }
  }
  return { oxigenio, temperatura }
}

// ---------------------------------------------------------------------------
// As duas famílias de medida — separadas na fila como no servidor
// ---------------------------------------------------------------------------

export interface LeituraDaFila {
  id: string
  hora: string | null
  temperatura_c: number | null
  oxigenio_mgl: number | null
  ceu: string | null
}

export interface AnaliseDaFila {
  id: string
  hora: string | null
  ph: number | null
  alcalinidade_mgl: number | null
}

/**
 * Oxigênio e temperatura de um lado; pH e alcalinidade de outro.
 *
 * pH e alcalinidade são ANÁLISE avulsa, em outra hora que a ronda (decisão do
 * gestor, 04/10/2026). O servidor já manda as duas famílias em listas
 * separadas; esta função faz o mesmo com o que ainda está na fila do aparelho.
 * Sem ela, uma análise de pH na fila entraria na lista de oxigênio, e o tanque
 * apareceria como «lido» na ronda sem ninguém ter medido oxigênio.
 *
 * Um registro antigo com as duas famílias (a v67 gravava junto) entra nas
 * duas listas, cada uma com a sua parte — e com o MESMO id, o que a tela
 * precisa saber na hora de desfazer.
 */
export function separarFamilias(
  itens: { id: string; corpo: Record<string, unknown> }[],
): { leituras: LeituraDaFila[]; analises: AnaliseDaFila[] } {
  const n = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)
  const hora = (v: unknown): string | null => (typeof v === 'string' ? v : null)
  const leituras: LeituraDaFila[] = []
  const analises: AnaliseDaFila[] = []
  for (const { id, corpo } of itens) {
    const temperatura = n(corpo['temperatura_c'])
    const oxigenio = n(corpo['oxigenio_mgl'])
    const ph = n(corpo['ph'])
    const alcalinidade = n(corpo['alcalinidade_mgl'])
    if (temperatura !== null || oxigenio !== null) {
      leituras.push({
        id,
        hora: hora(corpo['hora']),
        temperatura_c: temperatura,
        oxigenio_mgl: oxigenio,
        ceu: typeof corpo['ceu'] === 'string' ? corpo['ceu'] : null,
      })
    }
    if (ph !== null || alcalinidade !== null) {
      analises.push({ id, hora: hora(corpo['hora']), ph, alcalinidade_mgl: alcalinidade })
    }
  }
  return { leituras, analises }
}

/**
 * A FILA SOMADA ÀS LEITURAS DO SERVIDOR, da mais recente para a mais antiga.
 *
 * É a ordem que `aguaDaConta` lê, e por isso uma ordem só para a Ronda, a
 * resposta enquanto se digita e o Trato. Três cuidados, cada um já mordeu:
 *   - ordena por DIA e HORA, não «pendente primeiro»: uma leitura da manhã
 *     gravada depois da da tarde não pode mandar só por ainda não ter subido;
 *   - compara só hora e minuto: a fila traz «15:40», o servidor «15:40:00», e
 *     o texto mais curto ordenaria como mais antigo;
 *   - a fila guarda da mais velha para a mais nova; invertida antes, duas
 *     leituras no mesmo minuto (a medida esquecida, completada logo em
 *     seguida) ficam na ordem certa — a ordenação mantém quem empata.
 * Leitura sem hora (a Perdas grava assim) fica por último no dia, como no
 * servidor (`NULLS LAST`).
 */
export function juntarComFila<T extends { data: string; hora: string | null }>(
  doServidor: T[],
  daFila: T[],
): T[] {
  const hm = (h: string | null) => (h ?? '').slice(0, 5)
  return [...[...daFila].reverse(), ...doServidor].sort((x, y) => {
    const dia = y.data.slice(0, 10).localeCompare(x.data.slice(0, 10))
    if (dia !== 0) return dia
    return hm(y.hora).localeCompare(hm(x.hora))
  })
}
